import {
  selectAppData,
  selectConnectionQualityByPeerID,
  selectIsPeerAudioEnabled,
  selectIsPeerVideoEnabled,
  selectPeerByID,
  selectPeerMetadata,
  selectScreenShareByPeerID,
  selectVideoTrackByPeerID,
  useHMSStore,
  useVideo,
} from "@100mslive/react-sdk";
import {
  Coffee,
  Hand,
  Maximize,
  MicOff,
  Minimize,
  WifiHigh,
  WifiLow,
  WifiOff,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PeerAvatar } from "@/components/common/PeerAvatar";
import { TileMenu } from "@/components/meeting/TileMenu";
import { useMirrorPreference } from "@/hooks/usePreferences";
import { cn } from "@/lib/utils";

const ConnectionIndicator = ({ peerId }) => {
  const quality = useHMSStore(selectConnectionQualityByPeerID(peerId));
  const score = quality?.downlinkQuality;
  if (score === undefined || score < 0) {
    return null;
  }
  const { Icon, label, tone } =
    score === 0
      ? { Icon: WifiOff, label: "Reconnecting", tone: "text-red-400" }
      : score <= 2
        ? { Icon: WifiLow, label: "Weak connection", tone: "text-amber-300" }
        : {
            Icon: WifiHigh,
            label: "Good connection",
            tone: "text-emerald-300",
          };
  return (
    <span
      data-testid="tile_network"
      role="img"
      title={label}
      aria-label={label}
      className={cn("rounded-full bg-black/40 p-1", tone)}
    >
      <Icon className="size-3.5" />
    </span>
  );
};

const TileLabel = ({ children, muted }) => (
  <div className="flex max-w-[calc(100%-1rem)] items-center gap-1.5 rounded-md bg-black/50 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm">
    {muted ? (
      <MicOff
        className="size-3.5 shrink-0 text-red-400"
        data-testid="participant_audio_mute_icon"
      />
    ) : null}
    <span className="truncate">{children}</span>
  </div>
);

/** A person's camera, or their initials, with their name and status. */
export const VideoTile = ({ peerId, className, showMenu = true }) => {
  const peer = useHMSStore(selectPeerByID(peerId));
  const videoTrack = useHMSStore(selectVideoTrackByPeerID(peerId));
  const isAudioOn = useHMSStore(selectIsPeerAudioEnabled(peerId));
  const isVideoOn = useHMSStore(selectIsPeerVideoEnabled(peerId));
  const metadata = useHMSStore(selectPeerMetadata(peerId)) || {};
  const isHeadless = useHMSStore(selectAppData("isHeadless"));
  const { videoRef } = useVideo({ trackId: videoTrack?.id });
  const [mirror] = useMirrorPreference();

  if (!peer) {
    return null;
  }
  const label = peer.isLocal ? `You (${peer.name})` : peer.name;
  const isMirrored =
    peer.isLocal && mirror && videoTrack?.facingMode !== "environment";

  return (
    <div
      data-testid={`participant_tile_${peer.name}`}
      className={cn(
        "group relative isolate overflow-hidden rounded-lg bg-tile text-white",
        className
      )}
    >
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        data-testid="participant_video_tile"
        className={cn(
          "size-full object-cover",
          isMirrored && "-scale-x-100",
          !isVideoOn && "invisible"
        )}
      />
      {!isVideoOn ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <PeerAvatar name={peer.name} data-testid="participant_avatar_icon" />
        </div>
      ) : null}
      <div className="absolute top-2 left-2 flex gap-1.5">
        {metadata.isHandRaised ? (
          <span
            className="rounded-full bg-amber-400 p-1.5 text-amber-950"
            title="Hand raised"
            data-testid="raiseHand_icon_onTile"
          >
            <Hand className="size-3.5" />
          </span>
        ) : null}
        {metadata.isBRBOn ? (
          <span
            className="rounded-full bg-sky-300 p-1.5 text-sky-950"
            title="Be right back"
            data-testid="brb_icon_onTile"
          >
            <Coffee className="size-3.5" />
          </span>
        ) : null}
      </div>
      {/* Top right, because your own video floats in the bottom right corner
          of the other person's tile. */}
      <div className="absolute top-2 right-2 flex items-center gap-1.5">
        <ConnectionIndicator peerId={peerId} />
        {showMenu && !peer.isLocal && !isHeadless ? (
          <TileMenu peer={peer} />
        ) : null}
      </div>
      <div className="absolute right-2 bottom-2 left-2 flex items-end">
        <TileLabel muted={!isAudioOn}>{label}</TileLabel>
      </div>
    </div>
  );
};

/** Someone's shared screen, letterboxed, with a fullscreen toggle. */
export const ScreenShareTile = ({ peerId, className }) => {
  const peer = useHMSStore(selectPeerByID(peerId));
  const track = useHMSStore(selectScreenShareByPeerID(peerId));
  const isHeadless = useHMSStore(selectAppData("isHeadless"));
  const { videoRef } = useVideo({ trackId: track?.id });
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () =>
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  if (!peer) {
    return null;
  }

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      containerRef.current?.requestFullscreen().catch(() => {});
    }
  };

  return (
    <div
      ref={containerRef}
      data-testid="screenshare_tile"
      className={cn(
        "group relative overflow-hidden rounded-lg bg-black text-white",
        className
      )}
    >
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="size-full object-contain"
      />
      <div className="absolute right-2 bottom-2 left-2 flex items-end justify-between gap-2">
        <TileLabel>
          {peer.isLocal ? "Your screen" : `${peer.name}'s screen`}
        </TileLabel>
        <div className="flex items-center gap-1.5">
          {document.fullscreenEnabled && !isHeadless ? (
            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              className="rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
            >
              {isFullscreen ? (
                <Minimize className="size-4" />
              ) : (
                <Maximize className="size-4" />
              )}
            </button>
          ) : null}
          {!peer.isLocal && !isHeadless ? (
            <TileMenu peer={peer} isScreenShare />
          ) : null}
        </div>
      </div>
    </div>
  );
};
